// Layer 3 (Transformation): the priority matrix — impact × effort, both axes
// derived from money the register already carries.
//
//   * Impact bands off the summed benefit: ≥ $100K High, $50–100K Medium,
//     below Low.
//   * Effort bands off capex: ≥ $250K High, $50–250K Medium, below Low.
//
// Neither axis is seeded — no impact or effort field exists on the seed row,
// and no discounted figure exists anywhere in the corpus, so the portfolio
// card is the sum of the benefit terms rather than a rate-and-horizon number
// invented to fill it.
//
// `improvementCell` is defined only for a priced row. An unpriced candidate
// has no benefit to band, so it is placed in no cell and counted on no matrix
// KPI — it appears in the candidate lane and at the tail of the register.

import { formatMoney } from '@/versions/v1/shared/NsrEngine';
import { improvementRegister, type ImprovementRow } from './Scene.transform';
import type { CostKpiCard } from './Scene.transform';

export type ImpactBand = 1 | 2 | 3;
export type EffortBand = 1 | 2 | 3;

export type ImprovementCell = {
  impact: ImpactBand;
  effort: EffortBand;
};

export const IMPACT_HIGH_FLOOR = 100_000;
export const IMPACT_MEDIUM_FLOOR = 50_000;
export const EFFORT_HIGH_FLOOR = 250_000;
export const EFFORT_MEDIUM_FLOOR = 50_000;

export function impactBand(benefitPerYear: number): ImpactBand {
  if (benefitPerYear >= IMPACT_HIGH_FLOOR) return 3;
  if (benefitPerYear >= IMPACT_MEDIUM_FLOOR) return 2;
  return 1;
}

export function effortBand(capex: number): EffortBand {
  if (capex >= EFFORT_HIGH_FLOOR) return 3;
  if (capex >= EFFORT_MEDIUM_FLOOR) return 2;
  return 1;
}

/** `null` for a row with no benefit term: an unpriced opportunity has no
 *  impact coordinate, so it does not sit in the grid at all. */
export function improvementCell(row: ImprovementRow): ImprovementCell | null {
  if (row.benefitTerms.length === 0) return null;
  return { impact: impactBand(row.benefitPerYearRaw), effort: effortBand(row.capexRaw) };
}

export function isQuickWin(row: ImprovementRow): boolean {
  const cell = improvementCell(row);
  return cell !== null && cell.effort === 1 && row.paybackRaw < 1;
}

export type MatrixCell = {
  impact: ImpactBand;
  effort: EffortBand;
  /** Row ids, in register order. */
  ids: readonly string[];
  /** The same rows with their names, for the cell's id-and-name list. */
  items: readonly { id: string; opportunity: string }[];
};

export type MatrixGrid = {
  /** Rows run impact 3 → 1 (top to bottom); each row's cells run effort 1 → 3. */
  rows: readonly { impact: ImpactBand; cells: readonly MatrixCell[] }[];
  cells: readonly MatrixCell[];
  impactTicks: readonly ImpactBand[];
  effortTicks: readonly EffortBand[];
};

export const MATRIX_IMPACT_TICKS: readonly ImpactBand[] = [3, 2, 1];
export const MATRIX_EFFORT_TICKS: readonly EffortBand[] = [1, 2, 3];

export const MATRIX_AXIS_LABELS: Record<1 | 2 | 3, string> = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
};

export function improvementMatrix(): MatrixGrid {
  const { rows } = improvementRegister();
  const cells: MatrixCell[] = [];
  const gridRows: { impact: ImpactBand; cells: readonly MatrixCell[] }[] = [];

  for (const impact of MATRIX_IMPACT_TICKS) {
    const rowCells: MatrixCell[] = [];
    for (const effort of MATRIX_EFFORT_TICKS) {
      const inCell = rows.filter((row) => {
        const cell = improvementCell(row);
        return cell !== null && cell.impact === impact && cell.effort === effort;
      });
      const ids = inCell.map((row) => row.id);
      const items = inCell.map((row) => ({ id: row.id, opportunity: row.opportunity }));
      const cell: MatrixCell = { impact, effort, ids, items };
      rowCells.push(cell);
      cells.push(cell);
    }
    gridRows.push({ impact, cells: rowCells });
  }

  return { rows: gridRows, cells, impactTicks: MATRIX_IMPACT_TICKS, effortTicks: MATRIX_EFFORT_TICKS };
}

export type MatrixTopRow = {
  id: string;
  opportunity: string;
  benefit: string;
  /** The row's benefit as a share of the top row's, for its rank bar. */
  ratio: number;
};

/** Ranked by benefit, not by payback — the opposite order to the register, and
 *  the point the two regions make when they disagree. */
export function topByBenefit(limit = 5): readonly MatrixTopRow[] {
  const ranked = [...improvementRegister().rows]
    .filter((row) => row.benefitTerms.length > 0)
    .sort((a, b) => b.benefitPerYearRaw - a.benefitPerYearRaw)
    .slice(0, limit);
  const max = ranked[0]?.benefitPerYearRaw ?? 0;
  return ranked.map((row) => ({
    id: row.id,
    opportunity: row.opportunity,
    benefit: row.benefitPerYear,
    ratio: max > 0 ? row.benefitPerYearRaw / max : 0,
  }));
}

export type ImprovementKpis = {
  priced: number;
  quickWins: number;
  totalBenefit: number;
  totalCapex: number;
  staged: number;
  total: number;
};

export function improvementKpis(): ImprovementKpis {
  const { rows } = improvementRegister();
  const priced = rows.filter((row) => improvementCell(row) !== null);
  return {
    priced: priced.length,
    quickWins: rows.filter(isQuickWin).length,
    totalBenefit: rows.reduce((sum, row) => sum + row.benefitPerYearRaw, 0),
    totalCapex: rows.reduce((sum, row) => sum + row.capexRaw, 0),
    staged: rows.filter((row) => row.stage === 'approved' || row.stage === 'capital request').length,
    total: rows.length,
  };
}

/** Counts, not states: every card is neutral (polish.P7). */
export function matrixKpis(): readonly CostKpiCard[] {
  const kpis = improvementKpis();
  return [
    {
      label: 'Priced',
      value: String(kpis.priced),
      sub: `${kpis.total} identified`,
      tone: 'neutral',
    },
    {
      label: 'Quick wins',
      value: String(kpis.quickWins),
      sub: 'Low effort, payback under a year',
      tone: 'neutral',
    },
    {
      label: 'Total benefit / yr',
      value: formatMoney(kpis.totalBenefit),
      sub: `${formatMoney(kpis.totalCapex)} capex`,
      tone: 'neutral',
    },
    {
      label: 'Approved or capital request',
      value: `${kpis.staged} of ${kpis.total}`,
      sub: 'Beyond a priced case',
      tone: 'neutral',
    },
  ];
}
